import { computed, nextTick, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useLoading } from '@sa/hooks';
import { fetchGetUserInfo, fetchLogin, fetchVerifyTotp } from '@/service/api';
import { useRouterPush } from '@/hooks/common/router';
import { localStg } from '@/utils/storage';
import { SetupStoreId } from '@/enum';
import { $t } from '@/locales';
import { useRouteStore } from '../route';
import { useTabStore } from '../tab';
import { clearAuthStorage, getTempToken, getToken, setTempToken } from './shared';

export const useAuthStore = defineStore(SetupStoreId.Auth, () => {
  const route = useRoute();
  const routeStore = useRouteStore();
  const tabStore = useTabStore();
  const { toLogin, redirectFromLogin } = useRouterPush(false);
  const { loading: loginLoading, startLoading, endLoading } = useLoading();

  const token = ref(getToken());
  const tempToken = ref(getTempToken() || ''); // 从本地存储中读取临时令牌，确保是字符串
  const requireTotp = ref(false); // 是否需要TOTP验证

  // 初始化时检查临时令牌
  if (tempToken.value) {
    requireTotp.value = true;
  }

  const userInfo: Api.Auth.UserInfo = reactive({
    user_id: '',
    user_name: '',
    user_nickname: '',
    roles: [],
    buttons: []
  });

  /** is super role in static route */
  const isStaticSuper = computed(() => {
    const { VITE_AUTH_ROUTE_MODE, VITE_STATIC_SUPER_ROLE } = import.meta.env;

    return VITE_AUTH_ROUTE_MODE === 'static' && userInfo.roles.includes(VITE_STATIC_SUPER_ROLE);
  });

  /** Is login */
  const isLogin = computed(() => Boolean(token.value));

  /** Reset auth store */
  async function resetStore() {
    const authStore = useAuthStore();

    recordUserId();

    clearAuthStorage();

    authStore.$reset();
    console.log('route.meta',route.meta)
    if (!route.meta.constant) {
      console.log('401 await toLogin()');
      await toLogin();
    }

    tabStore.cacheTabs();
    routeStore.resetStore();
  }

  /** Record the user ID of the previous login session Used to compare with the current user ID on next login */
  function recordUserId() {
    if (!userInfo.user_id) {
      return;
    }

    // Store current user ID locally for next login comparison
    localStg.set('lastLoginUserId', userInfo.user_id);
  }

  /**
   * Check if current login user is different from previous login user If different, clear all tabs
   *
   * @returns {boolean} Whether to clear all tabs
   */
  function checkTabClear(): boolean {
    if (!userInfo.user_id) {
      return false;
    }

    const lastLoginUserId = localStg.get('lastLoginUserId');

    // Clear all tabs if current user is different from previous user
    if (!lastLoginUserId || lastLoginUserId !== userInfo.user_id) {
      localStg.remove('globalTabs');
      tabStore.clearTabs();

      localStg.remove('lastLoginUserId');
      return true;
    }

    localStg.remove('lastLoginUserId');
    return false;
  }

  /**
   * Login
   *
   * @param username User name
   * @param password Password
   * @param [redirect=true] Whether to redirect after login. Default is `true`
   */
  async function login(username: string, password: string, redirect = true) {
    startLoading();
    clearAuthStorage();// 清除本地存储的token和refreshToken

    try {
      const { data: loginResponse, error } = await fetchLogin(username, password);

      if (!error && loginResponse) {
        if (loginResponse.code === 0) {
          if (loginResponse.data?.require_totp) {
            // 需要TOTP验证
            requireTotp.value = true;
            tempToken.value = loginResponse.data.temp_token || '';
            setTempToken(tempToken.value); // 保存到本地存储

            window.$notification?.info({
              title: $t('page.login.common.loginSuccess'),
              content: '需要双因素认证验证',
              duration: 3000
            });

            endLoading();
            return { requireTotp: true, tempToken: tempToken.value };
          }
          // 直接登录成功
          const pass = await loginByToken({
            token: loginResponse.data?.access_token || '',
            refreshToken: loginResponse.data?.refresh_token || '',
            expires_in: loginResponse.data?.expires_in
          });

          if (pass) {
            // Check if the tab needs to be cleared
            const isClear = checkTabClear();
            let needRedirect = redirect;

            if (isClear) {
              // If the tab needs to be cleared,it means we don't need to redirect.
              needRedirect = false;
            }
            await redirectFromLogin(needRedirect);

            window.$notification?.success({
              title: $t('page.login.common.loginSuccess'),
              content: $t('page.login.common.welcomeBack', { userName: userInfo.user_nickname }),
              duration: 4500
            });

            endLoading();
            return { success: true };
          }
        } else {
          // 登录失败
          window.$notification?.error({
            title: '登录失败',
            content: loginResponse.msg || '用户名或密码错误',
            duration: 3000
          });
        }
      } else {
        // 只有在非TOTP验证的情况下才重置store
        if (!requireTotp.value) {
          resetStore();
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      window.$notification?.error({
        title: '登录失败',
        content: '网络错误，请稍后重试',
        duration: 3000
      });
    }

    endLoading();
    return { success: false };
  }

  /**
   * Verify TOTP
   *
   * @param totpCode TOTP code
   * @param [redirect=true] Whether to redirect after verification. Default is `true`
   */
  async function verifyTotp(totpCode: string, redirect = true) {
    if (!tempToken.value) {
      window.$notification?.error({
        title: '验证失败',
        content: '临时令牌无效',
        duration: 3000
      });
      return false;
    }

    startLoading();

    try {
      const { data: totpResponse, error } = await fetchVerifyTotp(tempToken.value, totpCode);

      if (!error && totpResponse && totpResponse.code === 0) {
        const pass = await loginByToken({
          token: totpResponse.data?.access_token || '',
          refreshToken: totpResponse.data?.refresh_token || '',
          expires_in: totpResponse.data?.expires_in
        });

        if (pass) {
          // 只有在验证成功时才清除临时令牌
          requireTotp.value = false;
          tempToken.value = '';
          sessionStorage.removeItem('tempToken'); // 清除本地存储的临时令牌

          // Check if the tab needs to be cleared
          const isClear = checkTabClear();
          let needRedirect = redirect;

          if (isClear) {
            needRedirect = false;
          }
          await redirectFromLogin(needRedirect);

          window.$notification?.success({
            title: '验证成功',
            content: $t('page.login.common.welcomeBack', { userName: userInfo.user_nickname }),
            duration: 4500
          });

          endLoading();
          return true;
        }
        // loginByToken 失败时，保留 tempToken
        window.$notification?.error({
          title: '验证失败',
          content: '获取用户信息失败，请重试',
          duration: 3000
        });
      } else {
        // 验证失败时，保留 tempToken，允许多次验证
        window.$notification?.error({
          title: '验证失败',
          content: totpResponse?.msg || '验证码错误',
          duration: 3000
        });
      }
    } catch (err) {
      console.error('TOTP verification error:', err);
      window.$notification?.error({
        title: '验证失败',
        content: '网络错误，请稍后重试',
        duration: 3000
      });
    }

    endLoading();
    return false;
  }

  async function loginByToken(loginToken: Api.Auth.LoginToken) {
    // 1. stored in the localStorage, the later requests need it in headers
    localStg.set('token', loginToken.token);
    localStg.set('refreshToken', loginToken.refreshToken);

    // 2. get user info
    const pass = await getUserInfo();

    if (pass) {
      token.value = loginToken.token;

      // 确保响应性更新
      await nextTick();

      return true;
    }

    // 如果获取用户信息失败，不要清除 tempToken
    return false;
  }

  async function getUserInfo() {
    const { data: response, error } = await fetchGetUserInfo();

    if (!error && response && response.code === 0) {
      // update store
      Object.assign(userInfo, response.data);

      return true;
    }

    return false;
  }

  async function initUserInfo() {
    const hasToken = getToken();

    if (hasToken) {
      const pass = await getUserInfo();

      if (!pass) {
        resetStore();
      }
    }
  }

  return {
    token,
    userInfo,
    isStaticSuper,
    isLogin,
    loginLoading,
    requireTotp,
    tempToken,
    resetStore,
    login,
    verifyTotp,
    initUserInfo
  };
});
